import { useEffect, useRef, useState } from 'react';

/**
 * A number that ROLLS to its new value rather than jumping to it.
 *
 * Ported from `~/projects/pwa`'s `AnimatedCount`, which is the architectural
 * model for this repo — the easing, the round-to-precision and the
 * cancel-on-change are that module's, and the shape they arrive in here is this
 * repo's: a hook in `application/motion/` beside `useRaceClock` and
 * `useRevealSequence`, with every browser API a defaulted parameter.
 *
 * WHY A SCORE SHOULD ROLL. The duel's score is the one number on any screen that
 * belongs to the reader, and a round that pays two hundred points used to move
 * it in a single frame — the reader saw a different number, not a number
 * changing. Rolling is what makes the payment a thing that HAPPENED: the digits
 * are still moving when the eye arrives, so the size of the jump is felt rather
 * than worked out by subtracting the last one you remember.
 *
 * `easeOutCubic`, so it opens fast and settles. A linear roll reads as a
 * loading spinner made of digits; a roll that eases IN would spend its first
 * frames looking broken.
 *
 * IT NEVER LIES ABOUT WHERE IT ENDED. The last frame is the target itself rather
 * than the eased approximation, and a value that changes mid-roll cancels the
 * frame in flight and re-aims from wherever the digits currently read. Two rounds
 * answered inside half a second must not leave a score that settles on the first
 * one's total.
 *
 * `now`, `schedule` and `cancel` are defaulted parameters rather than globals
 * reached for inside, per the repo's browser-API rule and for the reason
 * `useRaceClock` states: a hook that called `requestAnimationFrame` itself could
 * not be stepped by a test, and jsdom does not run frames on its own. A
 * `schedule` that answers null — no rAF at all, which is any server render —
 * settles on the value immediately, because a number that never arrived is worse
 * than one that arrived without ceremony.
 */

/**
 * Long enough to read as a roll, short enough that the reader is never waiting
 * for it. The reveal's own bands land over `--duration-reveal` (480ms), and a
 * score still climbing after the answer has finished explaining itself would be
 * the screen holding two conversations.
 */
export const COUNT_UP_MILLISECONDS = 420;

const defaultNow = () => (typeof performance === 'undefined' ? Date.now() : performance.now());
const defaultSchedule = (callback) => (
    typeof requestAnimationFrame === 'undefined' ? null : requestAnimationFrame(callback)
);
const defaultCancel = (handle) => {
    if (typeof cancelAnimationFrame !== 'undefined' && handle !== null) {
        cancelAnimationFrame(handle);
    }
};

function easeOutCubic(progress) {
    return 1 - ((1 - progress) ** 3);
}

/** A finite number, or null for everything that is not one. */
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export default function useCountUp(value, {
    duration = COUNT_UP_MILLISECONDS,
    now = defaultNow,
    schedule = defaultSchedule,
    cancel = defaultCancel,
} = {}) {
    const target = finite(value);
    const [shown, setShown] = useState(() => target ?? 0);
    // What the digits currently READ, which is not the same as `shown` during a
    // render pass and is what a re-aim has to start from.
    const rendered = useRef(target ?? 0);
    const frame = useRef(null);
    // The first value is arrived at rather than rolled to: a screen that opened
    // mid-run and counted up to the score from zero would be announcing a
    // payment that happened before the reader got here.
    const isFirst = useRef(true);

    useEffect(() => {
        cancel(frame.current);
        frame.current = null;

        if (target === null) {
            return undefined;
        }

        const from = isFirst.current ? target : rendered.current;

        isFirst.current = false;

        if (from === target || duration <= 0) {
            rendered.current = target;
            setShown(target);

            return undefined;
        }

        const startedAt = now();
        const step = (frameTime = now()) => {
            const elapsed = (typeof frameTime === 'number' ? frameTime : now()) - startedAt;
            const progress = Math.min(1, Math.max(0, elapsed / duration));
            const next = progress >= 1
                ? target
                : Math.round(from + ((target - from) * easeOutCubic(progress)));

            rendered.current = next;
            setShown(next);

            frame.current = progress >= 1 ? null : schedule(step);
        };

        frame.current = schedule(step);

        // A platform with no frame loop at all settles rather than freezing on
        // the value it started from.
        if (frame.current === null) {
            rendered.current = target;
            setShown(target);
        }

        return () => {
            cancel(frame.current);
            frame.current = null;
        };
    }, [target, duration, now, schedule, cancel]);

    return target === null ? value : shown;
}
